/**
 * Notification Bell Component
 *
 * A header button that displays the notification bell icon with an unread count badge.
 * Opens a dropdown panel showing recent notifications.
 *
 * @usage
 * <ng-notification-bell></ng-notification-bell>
 */

import { Component, OnInit, OnDestroy, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatBadgeModule } from '@angular/material/badge';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatMenuModule, MatMenu } from '@angular/material/menu';
import { Subject, takeUntil } from 'rxjs';
import { NotificationService } from '../../../shared/services/notification.service';
import { NotificationPanelComponent } from '../notification-panel/notification-panel.component';

@Component({
    selector: 'newgenus-notification-bell',
    imports: [CommonModule, MatBadgeModule, MatButtonModule, MatIconModule, MatMenuModule, NotificationPanelComponent],
    template: `
        <button
            mat-icon-button
            [matMenuTriggerFor]="notificationMenu"
            aria-label="Notifications"
            class="notification-bell-button"
            (menuOpened)="onMenuOpened()"
        >
            <mat-icon
                [matBadge]="unreadCount"
                [matBadgeHidden]="unreadCount === 0"
                matBadgeColor="warn"
                matBadgeSize="small"
                aria-hidden="false"
            >
                {{ unreadCount > 0 ? 'notifications_active' : 'notifications' }}
            </mat-icon>
        </button>

        <mat-menu #notificationMenu="matMenu" class="notification-menu" [overlapTrigger]="false">
            <newgenus-notification-panel (closePanel)="closeMenu()"></newgenus-notification-panel>
        </mat-menu>
    `,
    styles: [
        `
            .notification-bell-button {
                position: relative;
            }

            /* The icon's own 24px box, here rather than only via Material's .mat-icon rule: on a cold load
           the header could render one frame with the ligature as plain text ("notifications", 120px),
           pushing the phone header 26px off-screen (e2e/issues/mobile-geometry, OE-213). Not
           overflow:hidden - the badge is a child of this element and sits outside its box. */
            .notification-bell-button mat-icon {
                width: 24px;
                height: 24px;
            }

            :host ::ng-deep .notification-menu {
                width: 420px !important;
                max-width: 420px !important;
                min-width: 420px !important;
            }

            :host ::ng-deep .notification-menu .mat-mdc-menu-content {
                padding: 0 !important;
                overflow: hidden !important;
            }

            @media (max-width: 480px) {
                :host ::ng-deep .notification-menu {
                    max-width: 100vw !important;
                    width: 100vw;
                }
            }
        `,
    ],
})
export class NotificationBellComponent implements OnInit, OnDestroy {
    @ViewChild('notificationMenu') notificationMenu!: MatMenu;

    unreadCount = 0;
    private readonly destroy$ = new Subject<void>();

    constructor(public notificationService: NotificationService) {}

    ngOnInit(): void {
        this.notificationService.unreadCount$.pipe(takeUntil(this.destroy$)).subscribe((count) => {
            this.unreadCount = count;
        });
    }

    onMenuOpened(): void {
        // Could trigger a refresh or analytics event here
    }

    closeMenu(): void {
        if (this.notificationMenu) {
            this.notificationMenu.closed.emit();
        }
    }

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }
}
